import {
  Alert,
  Button,
  Col,
  Collapse,
  Descriptions,
  Form,
  Input,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
  Image,
  message,
} from "antd";
import { useSearchParams } from "react-router-dom";
import { CompanyForm } from "./CompanyView.default";
import { useEffect, useState, useRef } from "react";
import {
  Company,
  CompanyIndustry,
  FiscalPeriod,
  MAX_PAGE_SIZE,
} from "../repository/data.default";
import {
  addCompany,
  deleteCompany,
  getCompanies,
  getCompany,
  getCompanyAssets,
  getCompanyCollaborators,
  getCompanyConsultants,
  getCompanyDepartments,
  getCompanyEmployees,
  getCompanyIndustries,
  getCompanyFiscalPeriods,
  getCompanyManagers,
  updateCompany,
} from "../repository/data.repository";
import { QueryOperation } from "../code/search-query/search-query";
import dayjs from "dayjs";
import View from "../controls/View";
import DynamicForm from "../controls/DynamicForm";
import useEntityEditor from "../hooks/useEntityEditor";
import { FieldConfig, renderFields } from "../controls/DescriptionsFields";
import PhoneInput, { formatPhone, phoneRule } from "../controls/PhoneInput";
import { retry } from "../utils/http";
import { nameOf } from "../utils/typescript";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import { faImage, faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getCompanyLogo,
  uploadCompanyLogo,
} from "../repository/data.repository";
import CompanyLogo from "../assets/images/apartment.svg";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { DynamicControl } from "../controls/DynamicForm.default";
import CompanyViewCharts from "./CompanyViewCharts";
import CompanySubCompaniesPanel from "./company-view/CompanySubCompaniesPanel";
import CompanyDepartmentsPanel from "./company-view/CompanyDepartmentsPanel";
import CompanyManagersPanel from "./company-view/CompanyManagersPanel";
import CompanyEmployeesPanel from "./company-view/CompanyEmployeesPanel";
import CompanyAffiliatePanel from "./company-view/CompanyAffiliatePanel";
import CompanyAssetsPanel from "./company-view/CompanyAssetsPanel";
import { useCompanyScope } from "./company-view/useCompanyScope";
import FiscalPeriodsView from "./FiscalPeriodsView";
import FiscalPeriodView from "./FiscalPeriodView";
import IncidentNotifiersView from "./IncidentNotifiersView";
import IncidentNotifierView from "./IncidentNotifierView";
import CompanyAssetsView from "./CompanyAssetsView";
import CompanyAssetView from "./CompanyAssetView";
import AssetMetricsView from "./AssetMetricsView";

export interface CompanyViewProps {
  readonly uid?: string | null;
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete" | "view";
  readonly onFinish?: () => void;
  readonly onClose?: () => void;
}

function CompanyView(props: CompanyViewProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<CompanyForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [item, setItem] = useState<Company | null>(null);
  const logoRef = useRef<HTMLInputElement>(null!);
  const [messageApi, contextHolder] = message.useMessage();
  const [logoLoading, setLogoLoading] = useState(false);
  // Which organization panel is currently expanded under the main card. `null` means nothing is
  // expanded yet — the operator picks a counter to open it. "users" opens all four user-type sub-panels.
  type PanelKey = "subCompanies" | "departments" | "users" | "assets";
  const [openPanel, setOpenPanel] = useState<PanelKey | null>(null);
  // Bumped every time a sub-panel confirms an add/assign — triggers the panel to re-fetch its own list
  // and the company card to re-read its counters.
  const [panelReloadToken, setPanelReloadToken] = useState(0);
  // Per-user-type counts reported by each sub-panel after it loads. Lets the Organization box show the
  // breakdown — "Users: 8" with chips "M 3 · E 5 · C 1 · Co 0" so Consultants/Collaborators (which are
  // cross-company and NOT counted in Company.usersCount) still have visible numbers.
  const [typeCounts, setTypeCounts] = useState<{
    managers?: number;
    employees?: number;
    consultants?: number;
    collaborators?: number;
  }>({});
  const togglePanel = (next: PanelKey) =>
    setOpenPanel((prev) => (prev === next ? null : next));

  // Walk the parent → children tree so every panel, counter and picker treats the opened company as
  // the ROOT OF ITS BRANCH rather than a single standalone row. Includes the root itself.
  const scope = useCompanyScope(item?.uid ?? undefined, item ?? undefined, panelReloadToken);
  const scopeUids = scope.uids;

  // Per-type user counts across the whole scope — operator sees the aggregate up front, without having
  // to open each sub-company's own card. `pageSize=1` because only `totalRecords` is needed.
  // Also recomputes departments / sub-companies / assets totals the same way, so the Organization box
  // shows the whole-branch picture instead of just the root's own direct children.
  const [branchCounts, setBranchCounts] = useState<{
    subCompanies?: number;
    departments?: number;
    assets?: number;
  }>({});
  useEffect(() => {
    if (!props.open || scopeUids.length === 0) return;
    let aborted = false;
    const countInScope = async (
      fn: (input: any) => Promise<{ totalRecords: number }>,
    ) => {
      try {
        const res = await fn({
          searchQuery: {
            conditions: [
              { field: "companyUid", values: scopeUids, operation: QueryOperation.In },
            ],
          },
          pageNumber: 0,
          pageSize: 1,
        });
        return res.totalRecords ?? 0;
      } catch {
        return 0;
      }
    };
    // Sub-companies count = everything in the scope except the root — the operator opened the root, so
    // showing "including itself" would be off by one.
    const subCompaniesCount = Math.max(scopeUids.length - 1, 0);
    Promise.all([
      countInScope(getCompanyManagers as any),
      countInScope(getCompanyEmployees as any),
      countInScope(getCompanyConsultants as any),
      countInScope(getCompanyCollaborators as any),
      countInScope(getCompanyDepartments as any),
      countInScope(getCompanyAssets as any),
    ]).then(([managers, employees, consultants, collaborators, departments, assets]) => {
      if (aborted) return;
      setTypeCounts({ managers, employees, consultants, collaborators });
      setBranchCounts({ subCompanies: subCompaniesCount, departments, assets });
    });
    return () => {
      aborted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeUids.join("|"), panelReloadToken, props.open]);

  // D: open the panel named in ?panel=... on first open so a deep-link / F5 keeps the operator where
  // they were. Writes back on toggle so sharing the current URL preserves the view too.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!props.open) return;
    const requested = searchParams.get("panel") as PanelKey | null;
    if (requested && ["subCompanies", "departments", "users", "assets"].includes(requested)) {
      setOpenPanel(requested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);
  useEffect(() => {
    // Only sync URL while the dialog is open — closing the dialog shouldn't leak the panel param back
    // to the grid route.
    if (!props.open) return;
    const next = new URLSearchParams(searchParams);
    if (openPanel) next.set("panel", openPanel);
    else next.delete("panel");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPanel]);
  const [logoSrc, setLogo] = useState(CompanyLogo);
  // True only when a real uploaded logo is shown (not the default placeholder) — gates the download action.
  const [hasLogo, setHasLogo] = useState(false);
  const permissions = useSelector(authorizeSelectors.permissions);
  // The company's fiscal periods, loaded once and shared by the analytics charts (for fiscal-period windows + comparisons).
  const [fiscalPeriods, setFiscalPeriods] = useState<FiscalPeriod[]>([]);
  // Fiscal Periods management (reachable from the company profile).
  const [fpManageOpen, setFpManageOpen] = useState(false);
  const [fpEditOpen, setFpEditOpen] = useState(false);
  const [fpMode, setFpMode] = useState<"add" | "update" | "delete">("add");
  const [fpUid, setFpUid] = useState<string | undefined>(undefined);
  const [fpTrigger, setFpTrigger] = useState("");
  // Incident Notifiers management (which company roles get notified per incident type).
  const [notifierManageOpen, setNotifierManageOpen] = useState(false);
  const [notifierEditOpen, setNotifierEditOpen] = useState(false);
  const [notifierMode, setNotifierMode] = useState<"add" | "update" | "delete">(
    "add",
  );
  const [notifierUid, setNotifierUid] = useState<string | undefined>(undefined);
  const [notifierTrigger, setNotifierTrigger] = useState("");
  // Company Assets management (vessels/aircraft/sites the company registers; crew map onto them).
  const [assetManageOpen, setAssetManageOpen] = useState(false);
  const [assetEditOpen, setAssetEditOpen] = useState(false);
  const [assetMode, setAssetMode] = useState<"add" | "update" | "delete">("add");
  const [assetUid, setAssetUid] = useState<string | undefined>(undefined);
  const [assetTrigger, setAssetTrigger] = useState("");
  const [assetMetricsOpen, setAssetMetricsOpen] = useState(false);
  const [assetMetricsRecord, setAssetMetricsRecord] = useState<{ uid?: string; name?: string }>({});

  // Inline editor for the company profile (reuses the same hook + generic Companies PATCH as the user edit).
  const editor = useEntityEditor({
    item: item as Record<string, any> | null,
    // The editor sends only the dirty fields (+ uid/rowVersion); the generic Companies PATCH applies them.
    updateFn: (input) => updateCompany(input as any),
    onSuccess: () => loadData(),
  });

  const infoFields: FieldConfig[] = [
    { key: "name", label: "Name", editable: true, input: <Input size="small" /> },
    { key: "address", label: "Address", editable: true, input: <Input size="small" /> },
    { key: "email", label: "Email", editable: true, input: <Input size="small" /> },
    {
      key: "phoneNumber",
      label: "Phone Number",
      editable: true,
      input: <PhoneInput size="small" />,
      rules: [phoneRule],
      format: formatPhone,
    },
    { key: "domainName", label: "Industry" },
    { key: "parentCompanyName", label: "Parent Company" },
  ];

  const registrationFields: FieldConfig[] = [
    {
      // Set once at company registration for future operators; not meant to be updated
      // (CompanyUpdateRequest intentionally omits it), so render read-only.
      key: "registrationNotes",
      label: "Registration Notes",
      span: 2,
    },
    {
      key: "createdOn",
      label: "Created On",
      format: (value) =>
        value ? dayjs(value).format("DD/MM/YYYY HH:mm") : undefined,
    },
  ];

  // Lifecycle

  useEffect(() => {
    if (props.open) {
      loadData();
    } else {
      reset();
    }
  }, [props.open, props.uid]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  // Events

  function onSubmit(): void {
    const {
      name,
      address,
      email,
      phoneNumber,
      domain,
      parentCompany,
      registrationNotes,
    } = values;
    setLoading(true);
    setError(false);
    if (props.mode === "add") {
      addCompany({
        name,
        address,
        email,
        phoneNumber,
        domainUid: domain.value,
        parentCompanyUid: parentCompany?.value,
        registrationNotes,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    } else if (props.mode === "update") {
      updateCompany({
        uid: item?.uid,
        rowVersion: item?.rowVersion,
        name,
        address,
        email,
        phoneNumber,
        // Industry & Parent Company are locked after registration — not sent on update.
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    } else if (props.mode === "delete") {
      deleteCompany(props?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    }
  }

  function onCancel(): void {
    props.onClose?.();
  }

  function onUploadLogo(e: React.ChangeEvent): void {
    const file = (e.target as any).files[0];
    if (file) {
      const mb = file.size / 1024 / 1024;
      const limit = 10;
      if (mb > limit) {
        messageApi.open({
          type: "error",
          content: "Logo size limit is 10mb",
        });
      } else {
        setLogoLoading(true);
        uploadCompanyLogo(file, props.uid!)
          .then(() => {
            setLogoLoading(false);
            // Reflect the new logo immediately (the GET endpoint would otherwise only refresh on reload).
            setLogo(URL.createObjectURL(file));
            setHasLogo(true);
            messageApi.open({
              type: "success",
              content: "Successfully uploaded logo",
            });
          })
          .catch(() => {
            setLogoLoading(false);
            messageApi.open({
              type: "error",
              content: "Failed to upload logo",
            });
          });
      }
    }
  }

  function onPickLogo(): void {
    logoRef.current.click();
  }

  async function onDownloadLogo(): Promise<void> {
    if (!hasLogo) return;
    try {
      const blob = await (await fetch(logoSrc)).blob();
      const ext = blob.type.split("/")[1] || "png";
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${item?.name ?? "company"}-logo.${ext}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      messageApi.open({ type: "error", content: "Failed to download logo" });
    }
  }

  // Methods

  function loadData(): void {
    if (props.uid) {
      setLoading(true);
      setError(false);
      Promise.all([
        getCompany(props.uid),
        getCompanyLogo(props.uid).catch(() => CompanyLogo),
        getCompanyFiscalPeriods({
          searchQuery: {
            conditions: [
              {
                field: "companyUid",
                operation: QueryOperation.Equal,
                values: [props.uid],
              },
            ],
          },
          pageNumber: 0,
          pageSize: MAX_PAGE_SIZE,
        }).catch(() => ({ data: [] }) as any),
      ])
        .then(([item, companyLogo, periods]) => {
          setLoading(false);
          setItem(item);
          setLogo(companyLogo);
          setHasLogo(companyLogo !== CompanyLogo);
          setFiscalPeriods(periods.data ?? []);

          if (props.mode !== "view") {
            form.setFieldsValue({
              uid: item.uid,
              rowVersion: item.rowVersion,
              address: item.address,
              email: item.email,
              name: item.name,
              phoneNumber: item.phoneNumber,
              domain: {
                display: item.domainName,
                value: item.domainUid,
              },
              parentCompany: {
                display: item.parentCompanyName,
                value: item.parentCompanyUid,
              },
            });
          }
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    } else {
      reset();
    }
  }

  function reset(): void {
    setValid(false);
    setError(false);
    setLoading(false);
    setLogo(CompanyLogo);
    setHasLogo(false);
    // Only reset fields while the dialog (and its <Form>s) are mounted; resetting an unmounted form
    // triggers antd's "useForm not connected to any Form element" warning and is a no-op anyway.
    if (props.open) {
      form.resetFields();
    }
  }

  function createMode(): string {
    switch (props.mode) {
      case "add":
        return "Add ";
      case "update":
        return "Save ";
      case "delete":
        return "Delete ";
    }

    return "";
  }

  const industriesRequest = (search: string) => {
    return retry(() =>
      getCompanyIndustries({
        searchQuery: {
          conditions: [
            {
              field: nameOf<CompanyIndustry>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  const companiesRequest = (search: string) => {
    return retry(() =>
      getCompanies({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Company>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  return (
    <>
      <View
        open={["view"].includes(props.mode) && props.open}
        loading={loading}
        title={"Company"}
        actions={
          permissions.can("Companies", "edit") &&
          (editor.editing ? (
            <Space>
              {editor.saveError && <Tag color="red">Save failed</Tag>}
              <Button onClick={editor.cancelEditing} disabled={editor.saving}>
                Cancel
              </Button>
              <Button
                type="primary"
                loading={editor.saving}
                onClick={editor.save}
              >
                Save
              </Button>
            </Space>
          ) : (
            <Button type="primary" onClick={() => editor.startEditing()}>
              Edit
            </Button>
          ))
        }
        onBack={onCancel}
      >
        <Form form={editor.form} component={false}>
          <div className="flex flex-col gap-4 p-4">
            {/* Header: logo + name / industry */}
            <div className="flex flex-row items-center gap-4">
              <div className="relative flex flex-col">
                <Image
                  width={128}
                  height={128}
                  src={logoSrc}
                  alt="Company Logo"
                />
                <input
                  className="hidden"
                  ref={logoRef}
                  hidden={true}
                  type="file"
                  accept="image/png, image/jpeg, image/svg+xml"
                  onChange={onUploadLogo}
                />
                <div className="absolute -bottom-2 -right-2 opacity-50 hover:opacity-100">
                  <Button
                    shape="circle"
                    size="small"
                    icon={<FontAwesomeIcon icon={faImage} />}
                    loading={logoLoading}
                    onClick={onPickLogo}
                    disabled={!permissions.can("Companies", "create")}
                  />
                </div>
                {hasLogo && (
                  <div className="absolute -bottom-2 -left-2 opacity-50 hover:opacity-100">
                    <Button
                      shape="circle"
                      size="small"
                      title="Download logo"
                      icon={<FontAwesomeIcon icon={faDownload} />}
                      onClick={onDownloadLogo}
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-col">
                <Typography.Title level={4} className="!mb-0">
                  {item?.name}
                </Typography.Title>
                <Typography.Text type="secondary">
                  {item?.domainName}
                </Typography.Text>
              </div>
            </div>

            {/* Info + Organization sections */}
            <div className="grid grid-cols-2 gap-4">
              <Descriptions
                title="Company Info"
                bordered
                size="small"
                column={1}
                items={renderFields(
                  infoFields,
                  item as Record<string, any> | null,
                  editor.editing,
                )}
              />

              <Descriptions
                title="Organization"
                bordered
                size="small"
                column={2}
                items={[
                  {
                    key: "tenantName",
                    label: "Tenant",
                    span: 2,
                    children: item?.tenantName,
                  },
                  {
                    key: "subCompaniesCount",
                    label: (
                      <Tooltip title="All sub-companies in this branch (direct + nested).">
                        <span>Sub Companies</span>
                      </Tooltip>
                    ) as any,
                    children: (
                      <Tag
                        color={openPanel === "subCompanies" ? "geekblue" : "blue"}
                        style={{ cursor: "pointer" }}
                        onClick={() => togglePanel("subCompanies")}
                      >
                        {branchCounts.subCompanies ?? item?.subCompaniesCount ?? 0}
                      </Tag>
                    ),
                  },
                  {
                    key: "departmentsCount",
                    label: (
                      <Tooltip title="Departments across this company and every sub-company in its branch.">
                        <span>Departments</span>
                      </Tooltip>
                    ) as any,
                    children: (
                      <Tag
                        color={openPanel === "departments" ? "geekblue" : "blue"}
                        style={{ cursor: "pointer" }}
                        onClick={() => togglePanel("departments")}
                      >
                        {branchCounts.departments ?? item?.departmentsCount ?? 0}
                      </Tag>
                    ),
                  },
                  {
                    key: "assetsCount",
                    label: (
                      <Tooltip title="Assets across this company and every sub-company in its branch.">
                        <span>Assets</span>
                      </Tooltip>
                    ) as any,
                    children: (
                      <Tag
                        color={openPanel === "assets" ? "geekblue" : "blue"}
                        style={{ cursor: "pointer" }}
                        onClick={() => togglePanel("assets")}
                      >
                        {branchCounts.assets ?? item?.assetsCount ?? 0}
                      </Tag>
                    ),
                  },
                  {
                    key: "usersCount",
                    label: (
                      <Tooltip title="Everyone across this company and every sub-company: Managers + Employees belong to a company; Consultants + Collaborators are cross-company affiliates assigned to one.">
                        <span>Users</span>
                      </Tooltip>
                    ) as any,
                    span: 2,
                    children: (
                      <Space wrap size={[6, 6]}>
                        <Tag
                          color={openPanel === "users" ? "geekblue" : "blue"}
                          style={{ cursor: "pointer" }}
                          onClick={() => togglePanel("users")}
                        >
                          Total: {(typeCounts.managers ?? 0) + (typeCounts.employees ?? 0) + (typeCounts.consultants ?? 0) + (typeCounts.collaborators ?? 0)}
                        </Tag>
                        <Tag color="blue">
                          Managers: {typeCounts.managers ?? "—"}
                        </Tag>
                        <Tag color="purple">
                          Employees: {typeCounts.employees ?? "—"}
                        </Tag>
                        <Tag color="cyan">
                          Consultants: {typeCounts.consultants ?? "—"}
                        </Tag>
                        <Tag color="orange">
                          Collaborators: {typeCounts.collaborators ?? "—"}
                        </Tag>
                      </Space>
                    ),
                  },
                ]}
              />
            </div>

            {/* Collapsible org panels — opens the list the operator clicked on in the Organization box.
                Each panel is self-contained; its own Add button reaches the same endpoints the standalone
                Companies / Departments / Users / Affiliates screens use, so nothing legacy is touched.
                In "add" mode the company doesn't exist yet, so there's nothing to list under it — skip
                rendering so a counter click opening an empty panel can't confuse the operator. */}
            {props.mode !== "add" && item?.uid && openPanel === "subCompanies" && (
              <CompanySubCompaniesPanel
                open
                parentCompanyUid={item?.uid}
                parentCompanyName={item?.name}
                reloadToken={panelReloadToken}
                onChange={() => {
                  setPanelReloadToken((t) => t + 1);
                  loadData();
                }}
                scopeCompanies={scope.companies}
              />
            )}
            {props.mode !== "add" && item?.uid && openPanel === "departments" && (
              <CompanyDepartmentsPanel
                open
                companyUid={item?.uid}
                companyName={item?.name}
                reloadToken={panelReloadToken}
                onChange={() => {
                  setPanelReloadToken((t) => t + 1);
                  loadData();
                }}
                scopeCompanies={scope.companies}
              />
            )}
            {props.mode !== "add" && item?.uid && openPanel === "users" && (
              <div className="flex flex-col gap-3">
                <CompanyManagersPanel
                  open
                  companyUid={item?.uid}
                  companyName={item?.name}
                  reloadToken={panelReloadToken}
                  onChange={() => {
                    setPanelReloadToken((t) => t + 1);
                    loadData();
                  }}
                  onCountLoaded={(n) =>
                    setTypeCounts((c) => ({ ...c, managers: n }))
                  }
                  scopeCompanies={scope.companies}
                />
                <CompanyEmployeesPanel
                  open
                  companyUid={item?.uid}
                  companyName={item?.name}
                  reloadToken={panelReloadToken}
                  onChange={() => {
                    setPanelReloadToken((t) => t + 1);
                    loadData();
                  }}
                  onCountLoaded={(n) =>
                    setTypeCounts((c) => ({ ...c, employees: n }))
                  }
                  scopeCompanies={scope.companies}
                />
                <CompanyAffiliatePanel
                  kind="consultant"
                  open
                  companyUid={item?.uid}
                  companyName={item?.name}
                  reloadToken={panelReloadToken}
                  onChange={() => {
                    setPanelReloadToken((t) => t + 1);
                    loadData();
                  }}
                  onCountLoaded={(n) =>
                    setTypeCounts((c) => ({ ...c, consultants: n }))
                  }
                  scopeCompanies={scope.companies}
                />
                <CompanyAffiliatePanel
                  kind="collaborator"
                  open
                  companyUid={item?.uid}
                  companyName={item?.name}
                  reloadToken={panelReloadToken}
                  onChange={() => {
                    setPanelReloadToken((t) => t + 1);
                    loadData();
                  }}
                  onCountLoaded={(n) =>
                    setTypeCounts((c) => ({ ...c, collaborators: n }))
                  }
                  scopeCompanies={scope.companies}
                />
              </div>
            )}
            {/* Assets panel — new scope-aware CompanyAssetsPanel. The legacy CompanyAssetsView is still
                reachable from the "Assets" descriptions section lower in the card, untouched. */}
            {props.mode !== "add" && item?.uid && openPanel === "assets" && (
              <CompanyAssetsPanel
                open
                companyUid={item?.uid}
                companyName={item?.name}
                reloadToken={panelReloadToken}
                onChange={() => {
                  setPanelReloadToken((t) => t + 1);
                  loadData();
                }}
                scopeCompanies={scope.companies}
              />
            )}

            {/* Registration section */}
            <Descriptions
              title="Registration"
              bordered
              size="small"
              column={2}
              items={renderFields(
                registrationFields,
                item as Record<string, any> | null,
                editor.editing,
              )}
            />

            {/* Fiscal Periods section — manage this company's quarters / semesters / years */}
            <Descriptions
              title="Fiscal Periods"
              bordered
              size="small"
              column={1}
              items={[
                {
                  key: "fiscalPeriods",
                  label: "Definitions",
                  children: (
                    <Space className="flex flex-row items-center justify-between">
                      <Typography.Text type="secondary">
                        Quarters / semesters / years used to present analytics on this
                        company's fiscal calendar.
                      </Typography.Text>
                      <Button
                        size="small"
                        type="link"
                        disabled={
                          !permissions.can("CompanyFiscalPeriods", "view") || !item?.uid
                        }
                        onClick={() => setFpManageOpen(true)}
                      >
                        Manage
                      </Button>
                    </Space>
                  ),
                },
              ]}
            />

            {/* Incident Notifiers section — which company roles get notified per incident type */}
            <Descriptions
              title="Incident Notifiers"
              bordered
              size="small"
              column={1}
              items={[
                {
                  key: "incidentNotifiers",
                  label: "Roster",
                  children: (
                    <Space className="flex flex-row items-center justify-between">
                      <Typography.Text type="secondary">
                        Company roles notified when an incident of a given type is
                        reported for this company.
                      </Typography.Text>
                      <Button
                        size="small"
                        type="link"
                        disabled={
                          !permissions.can(
                            "CompanyIncidentTypeNotifiers",
                            "view",
                          ) || !item?.uid
                        }
                        onClick={() => setNotifierManageOpen(true)}
                      >
                        Manage
                      </Button>
                    </Space>
                  ),
                },
              ]}
            />

            <Descriptions
              title="Assets"
              bordered
              size="small"
              column={1}
              items={[
                {
                  key: "assets",
                  label: "Register",
                  children: (
                    <Space className="flex flex-row items-center justify-between">
                      <Typography.Text type="secondary">
                        Physical assets this company operates (vessels, aircraft,
                        sites…) — employees are mapped onto them for per-asset metrics.
                      </Typography.Text>
                      <Button
                        size="small"
                        type="link"
                        disabled={
                          !permissions.can("CompanyAssets", "view") || !item?.uid
                        }
                        onClick={() => setAssetManageOpen(true)}
                      >
                        Manage
                      </Button>
                    </Space>
                  ),
                },
              ]}
            />

            {/* Analytics charts in an accordion at the bottom */}
            <Collapse
              accordion
              destroyInactivePanel
              items={[
                {
                  key: "analytics",
                  label: "Analytics",
                  children: (
                    <CompanyViewCharts
                      companyUid={item?.uid ?? ""}
                      fiscalPeriods={fiscalPeriods}
                    />
                  ),
                },
              ]}
            />
          </div>
        </Form>
      </View>
      <InModal
        open={["add", "update", "delete"].includes(props.mode) && props.open}
        title={modalTitle(props.mode, "Company", undefined)}
        onCancel={onCancel}
        size={"small"}
        actions={
          <Space className="sticky bottom-0 flex flex-row items-center justify-end">
            <Button disabled={loading} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type={"primary"}
              danger={props.mode === "delete"}
              loading={loading}
              disabled={!valid && props.mode !== "delete"}
              onClick={onSubmit}
            >
              {createMode()}
            </Button>
          </Space>
        }
      >
        <Col className="flex flex-col">
          {props.mode !== "delete" && (
            <DynamicForm
              className="w-auto"
              form={form}
              schema={{
                groups: [
                  {
                    controls: [
                      {
                        type: "combobox",
                        name: "parentCompany",
                        label: "Parent Company",
                        // Set-once at registration — locked on edit to protect provisioned structure.
                        disabled: props.mode === "update",
                        request: companiesRequest,
                      },
                      {
                        type: "textbox",
                        name: "name",
                        label: "Name",
                        rules: [
                          {
                            required: true,
                            message: "Name is required",
                          },
                        ],
                      },
                      {
                        type: "textbox",
                        name: "address",
                        label: "Address",
                      },
                      {
                        type: "textbox",
                        name: "email",
                        label: "Email",
                      },
                      {
                        type: "phonebox",
                        name: "phoneNumber",
                        label: "Phone Number",
                        rules: [phoneRule],
                      },
                      {
                        type: "combobox",
                        name: "domain",
                        label: "Industry",
                        // Set-once at registration — drives default provisioning; locked on edit.
                        disabled: props.mode === "update",
                        rules: [
                          {
                            required: true,
                            message: "Industry is required",
                          },
                        ],
                        request: industriesRequest,
                      },
                      ...((props.mode === "add"
                        ? [
                            {
                              type: "areabox",
                              name: "registrationNotes",
                              label: "Registration notes",
                            },
                          ]
                        : []) as DynamicControl[]),
                    ],
                  },
                ],
              }}
            />
          )}
          {props.mode === "delete" && (
            <Typography.Paragraph>
              <Typography.Text>
                Continue deleting the{" "}
                <Typography.Text className="font-bold">
                  {item?.name}
                </Typography.Text>{" "}
                company?
              </Typography.Text>
            </Typography.Paragraph>
          )}
          {error && (
            <Alert
              className="mb-2"
              message="Something went wrong, try again"
              type="error"
            />
          )}
        </Col>
      </InModal>

      {/* Manage this company's fiscal periods */}
      <InModal
        open={fpManageOpen}
        title="Manage Fiscal Periods"
        size="extraLarge"
        onCancel={() => setFpManageOpen(false)}
        actions={
          <Space className="flex w-full flex-row items-center justify-between">
            <Button
              type="primary"
              disabled={!permissions.can("CompanyFiscalPeriods", "create")}
              onClick={() => {
                setFpMode("add");
                setFpUid(undefined);
                setFpEditOpen(true);
              }}
            >
              Add Fiscal Period
            </Button>
            <Button onClick={() => setFpManageOpen(false)}>Close</Button>
          </Space>
        }
      >
        {item?.uid && (
          <div className="flex h-[480px] flex-col">
            <FiscalPeriodsView
              companyUid={item.uid}
              trigger={fpTrigger}
              onAction={(key, record) => {
                setFpMode(key as "update" | "delete");
                setFpUid(record.uid);
                setFpEditOpen(true);
              }}
            />
          </div>
        )}
      </InModal>

      {/* Add / edit / delete a single fiscal period */}
      {item?.uid && (
        <FiscalPeriodView
          companyUid={item.uid}
          uid={fpUid}
          mode={fpMode}
          open={fpEditOpen}
          onFinish={() => {
            setFpEditOpen(false);
            setFpTrigger(`${Date.now()}`);
          }}
          onClose={() => setFpEditOpen(false)}
        />
      )}

      {/* Manage this company's incident notify roster */}
      <InModal
        open={notifierManageOpen}
        title="Manage Incident Notifiers"
        size="extraLarge"
        onCancel={() => setNotifierManageOpen(false)}
        actions={
          <Space className="flex w-full flex-row items-center justify-between">
            <Button
              type="primary"
              disabled={
                !permissions.can("CompanyIncidentTypeNotifiers", "create")
              }
              onClick={() => {
                setNotifierMode("add");
                setNotifierUid(undefined);
                setNotifierEditOpen(true);
              }}
            >
              Add Notifier
            </Button>
            <Button onClick={() => setNotifierManageOpen(false)}>Close</Button>
          </Space>
        }
      >
        {item?.uid && (
          <div className="flex h-[480px] flex-col">
            <IncidentNotifiersView
              companyUid={item.uid}
              trigger={notifierTrigger}
              onAction={(key, record) => {
                setNotifierMode(key as "update" | "delete");
                setNotifierUid(record.uid);
                setNotifierEditOpen(true);
              }}
            />
          </div>
        )}
      </InModal>

      {/* Add / edit / delete a single incident notifier */}
      {item?.uid && (
        <IncidentNotifierView
          companyUid={item.uid}
          uid={notifierUid}
          mode={notifierMode}
          open={notifierEditOpen}
          onFinish={() => {
            setNotifierEditOpen(false);
            setNotifierTrigger(`${Date.now()}`);
          }}
          onClose={() => setNotifierEditOpen(false)}
        />
      )}

      {/* Manage this company's assets register */}
      <InModal
        open={assetManageOpen}
        title="Manage Assets"
        size="extraLarge"
        onCancel={() => setAssetManageOpen(false)}
        actions={
          <Space className="flex w-full flex-row items-center justify-between">
            <Button
              type="primary"
              disabled={!permissions.can("CompanyAssets", "create")}
              onClick={() => {
                setAssetMode("add");
                setAssetUid(undefined);
                setAssetEditOpen(true);
              }}
            >
              Add Asset
            </Button>
            <Button onClick={() => setAssetManageOpen(false)}>Close</Button>
          </Space>
        }
      >
        {item?.uid && (
          <div className="flex h-[480px] flex-col">
            <CompanyAssetsView
              companyUid={item.uid}
              trigger={assetTrigger}
              onAction={(key, record) => {
                if (key === "metrics") {
                  setAssetMetricsRecord({ uid: record.uid, name: record.name });
                  setAssetMetricsOpen(true);
                  return;
                }
                setAssetMode(key as "update" | "delete");
                setAssetUid(record.uid);
                setAssetEditOpen(true);
              }}
            />
          </div>
        )}
      </InModal>

      {/* Add / edit / delete a single asset */}
      {item?.uid && (
        <CompanyAssetView
          companyUid={item.uid}
          uid={assetUid}
          mode={assetMode}
          open={assetEditOpen}
          onFinish={() => {
            setAssetEditOpen(false);
            setAssetTrigger(`${Date.now()}`);
          }}
          onClose={() => setAssetEditOpen(false)}
        />
      )}

      <AssetMetricsView
        open={assetMetricsOpen}
        onClose={() => setAssetMetricsOpen(false)}
        assetUid={assetMetricsRecord.uid}
        assetName={assetMetricsRecord.name}
      />

      {contextHolder}
    </>
  );
}

export default CompanyView;
